import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, TriangleAlert, X } from "lucide-react";
import { api } from "@/lib/api";
import "./plan-b.css";

const BRANDS = ["CMP Products", "Hawke International", "Peppers Cable Glands", "CCG", "Prysmian",
  "Eaton Crouse-Hinds", "ABB", "Schneider Electric", "Pepperl+Fuchs", "Bartec", "Warom", "R.Stahl"];

const STEPS = [
  ["01", "Search by part number", "Type a manufacturer's part number or browse a category. Every listing carries its real technical specification, not a marketing blurb."],
  ["02", "Read the equivalents", "We show parts from other manufacturers against the same requirement, each marked Meets, Deviation or Does Not Meet — field by field."],
  ["03", "Raise one RFQ", "Tick every acceptable equivalent and send a single structured RFQ. Matching suppliers are notified automatically."],
  ["04", "Compare landed cost", "Quotations arrive structured: unit price, freight, duty, availability and where the stock physically sits today."],
];

const SPEC_ROWS = [
  ["Thread entry", "M20 × 1.5", "ok"],
  ["Ex protection", "Ex db IIC / Ex eb IIC", "ok"],
  ["Ingress protection", "IP66 / IP68", "ok"],
  ["Cable Ø range", "6.1 – 11.7 mm", "warn"],
  ["Certificate", "IECEx / ATEX", "ok"],
  ["Body material", "Nickel-plated brass", "bad"],
];

const VERDICT = {
  ok: { Icon: Check, cls: "text-emerald-400", label: "Meets" },
  warn: { Icon: TriangleAlert, cls: "text-amber", label: "Deviation" },
  bad: { Icon: X, cls: "text-rose-400", label: "No match" },
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setShown(true),
      { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`pb-reveal ${shown ? "pb-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const Counter = ({ to, suffix = "" }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (t) => {
        const p = Math.min((t - start) / 900, 1);
        setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to]);
  return <span ref={ref}>{n}{suffix}</span>;
};

export default function HomePlanB() {
  const [stats, setStats] = useState(null);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => setStats({}));
    api.get("/manufacturers").then(({ data }) => setBrands(
      (Array.isArray(data) ? data : data.results || [])
        .map((m) => m.name)
        // skip import artefacts such as BULK_Mfr_<id> so the public page stays clean
        .filter((n) => n && !/^BULK[_-]/i.test(n) && !/_[0-9a-f]{8,}/i.test(n))
    )).catch(() => {});
  }, []);

  // Only real, non-zero counts are ever shown.
  const bands = [
    [stats?.products, "+", "technical listings", "Every entry carries its normalised specification and certificate reference."],
    [stats?.categories, "", "product categories", "Glands, lighting, enclosures, cables, breakers, valves and more — all schema-driven."],
    [stats?.manufacturers, "", "manufacturers mapped", "Cross-referenced so an equivalent is a verdict, not a guess."],
  ].filter(([v]) => Number(v) > 0);

  return (
    <div className="pb-root font-body text-ink antialiased" data-testid="plan-b-home">
      <main className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <section className="relative grid items-center gap-14 pb-16 pt-24 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            {stats?.categories > 0 && (
              <div className="pb-fade-up mb-7 inline-flex items-center gap-2.5 rounded-full border border-wire/25 bg-wire/[0.06] px-3 py-1.5 font-mono text-[12px] text-wire"
                data-testid="pb-live-badge">
                <span className="pb-pulse h-1.5 w-1.5 rounded-full bg-amber" />
                live — {stats.categories} product categories
              </div>
            )}
            <h1 className="pb-fade-up pb-d1 max-w-[13ch] font-display text-[44px] font-bold leading-[1.02] tracking-[-0.025em] text-white sm:text-[54px] lg:text-[58px]">
              Sourcing built on specification, not guesswork.
            </h1>
            <p className="pb-fade-up pb-d2 mt-7 max-w-[46ch] text-[17px] leading-relaxed text-ink-dim">
              Find a part, read its real technical specification, and see which products from other
              manufacturers genuinely meet the same requirement — before you buy.
            </p>
            <div className="pb-fade-up pb-d3 mt-10 flex flex-wrap gap-3.5">
              <Link to="/rfq" data-testid="pb-hero-rfq"
                className="pb-lift pb-amber-glow group inline-flex items-center gap-2 rounded-lg bg-amber px-7 py-3.5 text-[15px] font-semibold text-[#1a1204] hover:bg-[#ffab2e]">
                Submit RFQ — Free
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/register?role=supplier" data-testid="pb-hero-supplier"
                className="pb-lift inline-flex items-center gap-2 rounded-lg border border-amber/40 bg-amber/[0.07] px-7 py-3.5 text-[15px] font-semibold text-amber hover:bg-amber/[0.12]">
                Join as a Founding Supplier
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="pb-fade-up pb-d4 mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13.5px]">
              <Link to="/search" data-testid="pb-hero-search"
                className="text-ink-dim underline decoration-white/15 underline-offset-4 transition-colors hover:text-white">
                Search products &amp; equivalents →
              </Link>
              <Link to="/register?role=buyer" data-testid="pb-hero-buyer-account"
                className="text-ink-faint underline decoration-white/15 underline-offset-4 transition-colors hover:text-ink-dim">
                Create a buyer account →
              </Link>
            </div>
          </div>

          <div className="pb-brackets pb-glass pb-float relative overflow-hidden rounded-2xl shadow-[0_40px_70px_-35px_rgba(0,0,0,0.7)]">
            <div className="pb-blueprint absolute inset-0" />
            <div className="relative p-6">
              <div className="flex items-center justify-between border-b pb-hairline pb-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-wire">
                    requirement · example
                  </p>
                  <p className="mt-1 font-display text-[15px] font-semibold text-white">CMP A2F M20 Ex db</p>
                </div>
                <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 font-mono text-[10px] text-emerald-400">
                  4 equivalents
                </span>
              </div>
              <div className="mt-4 space-y-0.5">
                {SPEC_ROWS.map(([label, value, verdict], i) => {
                  const { Icon, cls, label: vl } = VERDICT[verdict];
                  return (
                    <div key={label}
                      className="pb-row flex items-center justify-between gap-4 rounded-lg px-3 py-2.5"
                      style={{ animationDelay: `${300 + i * 90}ms` }}>
                      <span className="text-[12.5px] text-ink-dim">{label}</span>
                      <span className="flex items-center gap-2.5 text-right">
                        <span className="font-mono text-[12px] text-white">{value}</span>
                        <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase ${cls}`}>
                          <Icon className="h-3.5 w-3.5" strokeWidth={2.5} /> {vl}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 flex items-center justify-between border-t pb-hairline pt-4">
                <span className="font-mono text-[11px] text-ink-faint">verdict</span>
                <span className="font-display text-[13px] font-semibold text-amber">
                  1 technical deviation — review before award
                </span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <div className="pb-ticker-wrap overflow-hidden border-y pb-hairline py-9">
        <p className="mx-auto mb-6 max-w-[1280px] px-6 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint lg:px-10">
          brands we cross-reference
        </p>
        <div className="pb-ticker-track flex w-max gap-14">
          {[...BRANDS, ...BRANDS].map((b, i) => (
            <span key={`${b}-${i}`} className="whitespace-nowrap font-display text-[15px] font-medium text-ink-faint transition-colors hover:text-ink-dim">
              {b}
            </span>
          ))}
        </div>
        {brands.length > 0 && (
          <p className="mx-auto mt-6 max-w-[1280px] px-6 font-mono text-[10px] text-ink-faint lg:px-10"
            data-testid="pb-mapped-note">
            {brands.length} manufacturer{brands.length === 1 ? "" : "s"} already mapped in the
            catalogue: {brands.slice(0, 6).join(" · ")}
          </p>
        )}
      </div>

      <main className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <section id="how" className="border-b pb-hairline py-20">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-wire">how it works</p>
            <h2 className="mt-3 max-w-[22ch] font-display text-[32px] font-bold leading-tight tracking-tight text-white">
              Four steps from part number to landed cost.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(([n, title, body], i) => (
              <Reveal key={n} delay={i * 110}>
                <div className="pb-card h-full rounded-2xl p-6">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-wire/30 bg-wire/[0.08] font-mono text-[12px] text-wire">
                    {n}
                  </span>
                  <div className="pb-node-line my-5" />
                  <h3 className="font-display text-[17px] font-semibold leading-snug text-white">{title}</h3>
                  <p className="mt-3 text-[13.5px] leading-relaxed text-ink-dim">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {bands.length > 0 && (
          <section className="grid gap-8 border-b pb-hairline py-20 md:grid-cols-3"
            data-testid="pb-stats-band">
            {bands.map(([v, sfx, label, body], i) => (
              <Reveal key={label} delay={i * 110}>
                <div className="pb-card rounded-2xl p-7">
                  <p className="font-display text-[42px] font-bold leading-none tracking-tight text-white">
                    <Counter to={Number(v)} suffix={sfx} />
                  </p>
                  <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-wire">{label}</p>
                  <p className="mt-4 text-[13.5px] leading-relaxed text-ink-dim">{body}</p>
                </div>
              </Reveal>
            ))}
          </section>
        )}

        <section className="grid gap-8 py-20 md:grid-cols-2">
          <Reveal>
            <div className="pb-brackets pb-card h-full rounded-2xl p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-wire">for buyers &amp; EPCs</p>
              <h3 className="mt-4 font-display text-[24px] font-bold leading-tight tracking-tight text-white">
                Stop paying for the wrong equivalent.
              </h3>
              <ul className="mt-6 space-y-3">
                {["Field-by-field verdicts against your requirement",
                  "One RFQ covering every acceptable equivalent",
                  "Landed cost with freight, duty and clearance",
                  "See where stock sits today and where it was made"].map((t) => (
                  <li key={t} className="flex gap-3 text-[13.5px] leading-relaxed text-ink-dim">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} /> {t}
                  </li>
                ))}
              </ul>
              <Link to="/rfq" data-testid="pb-buyer-cta"
                className="pb-lift pb-amber-glow mt-8 inline-flex items-center gap-2 rounded-lg bg-amber px-6 py-3 text-[14px] font-semibold text-[#1a1204] hover:bg-[#ffab2e]">
                Submit RFQ — Free <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="pb-brackets pb-amber-brackets pb-card h-full rounded-2xl p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber">for suppliers</p>
              <h3 className="mt-4 font-display text-[24px] font-bold leading-tight tracking-tight text-white">
                Quote the enquiries you can actually win.
              </h3>
              <ul className="mt-6 space-y-3">
                {["Matched to RFQs by brand, category and market",
                  "Upload a PDF catalogue — AI drafts your listings",
                  "Structured quotations, no email ping-pong",
                  "Billed in AED with UAE VAT invoices"].map((t) => (
                  <li key={t} className="flex gap-3 text-[13.5px] leading-relaxed text-ink-dim">
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-amber" strokeWidth={2.5} /> {t}
                  </li>
                ))}
              </ul>
              <Link to="/register?role=supplier" data-testid="pb-supplier-cta"
                className="pb-lift mt-8 inline-flex items-center gap-2 rounded-lg border border-amber/40 bg-amber/[0.07] px-6 py-3 text-[14px] font-semibold text-amber hover:bg-amber/[0.12]">
                Join as a Founding Supplier <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/join-supplier" data-testid="pb-supplier-learn"
                className="mt-4 block text-[13px] text-ink-faint underline decoration-white/15 underline-offset-4 hover:text-ink-dim">
                How supplier matching works →
              </Link>
            </div>
          </Reveal>
        </section>
      </main>
    </div>
  );
}
